import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Pagination } from '@/components/ui/Pagination';
import { FormField } from '@/components/ui/FormField';
import { toast } from '@/store/toastStore';
import {
  kbQueryKeys,
  listKbSyncEvents,
  retryKbSyncEvent,
  type KbSyncEventListParams,
} from '@/services/knowledge-base.service';

function statusBadge(status: string) {
  if (status === 'success') return <Badge variant="success">Success</Badge>;
  if (status === 'failed') return <Badge variant="error">Failed</Badge>;
  return <Badge variant="neutral">{status}</Badge>;
}

export function AuditPage() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 50;

  const params: KbSyncEventListParams = useMemo(
    () => ({ status: status || undefined, page, perPage }),
    [status, page],
  );

  const eventsQuery = useQuery({
    queryKey: kbQueryKeys.syncEvents(params),
    queryFn: () => listKbSyncEvents(params),
  });

  const retryMutation = useMutation({
    mutationFn: (id: number) => retryKbSyncEvent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.syncEvents() });
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.documents() });
      toast.success('Retry succeeded');
    },
    onError: (err: Error) => toast.error(err.message || 'Retry failed'),
  });

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-slate-200 bg-white p-3">
        <FormField label="Status">
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            className="form-input-base w-48"
          >
            <option value="">All</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
          </select>
        </FormField>
      </div>

      {eventsQuery.isLoading ? (
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500">Loading...</div>
      ) : !eventsQuery.data?.data.length ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
          No sync events found.
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">When</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Source</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">External ID</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Action</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Status</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {eventsQuery.data.data.map((e) => (
                  <tr key={e.kbSyncEventId} className="hover:bg-slate-50">
                    <td className="px-4 py-2 text-xs text-slate-500">
                      {e.createdAt ? new Date(e.createdAt).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-2 text-sm text-slate-700">{e.sourceName || `#${e.kbSourceId}`}</td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-600">{e.externalId || '—'}</td>
                    <td className="px-4 py-2 text-sm text-slate-700">{e.action}</td>
                    <td className="px-4 py-2">
                      {statusBadge(e.status)}
                      {e.message && (
                        <div className="mt-1 max-w-md truncate text-xs text-slate-500" title={e.message}>
                          {e.message}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {e.status === 'failed' && e.hasPayload ? (
                        <Button
                          size="xs"
                          variant="secondary"
                          leftIcon={<RefreshCw className="h-3.5 w-3.5" />}
                          onClick={() => retryMutation.mutate(e.kbSyncEventId)}
                          loading={retryMutation.isPending}
                        >
                          Retry
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            page={page}
            total={eventsQuery.data.total}
            pageSize={perPage}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}
